import { useQuery } from '@tanstack/vue-query';
import type { Database } from '~/types';
import type { Tables } from '~/types/database-helpers';

export type SearchMode = 'players' | 'teams';

interface SearchParams {
  searchTerm: string;
}

const MIN_SEARCH_TERM_LENGTH = 2;

export function useHomePageSearch(params: SearchParams, mode: SearchMode) {
  const supabase = useSupabaseClient<Database>();

  const playersQuery = useQuery({
    queryKey: ['home-page-search', 'players', params],
    queryFn: async () => {
      if (!params.searchTerm || params.searchTerm.length < MIN_SEARCH_TERM_LENGTH) {
        return { data: [], count: 0 };
      }

      const { data, count, error } = await supabase
        .from('players_list')
        .select('*', { count: 'exact' })
        .ilike('player_name', `%${params.searchTerm.value}%`)
        .eq('year', 2025)
        .limit(5000)
        .order('player_name', { ascending: true });

      if (error) throw error;
      return { data, count };
    },
    enabled: computed(() => {
      return mode.value === 'players' && params.searchTerm.value.length >= MIN_SEARCH_TERM_LENGTH;
    }),
  });

  const teamsQuery = useQuery({
    queryKey: ['home-page-search', 'teams', params],
    queryFn: async () => {
      if (!params.searchTerm || params.searchTerm.length < MIN_SEARCH_TERM_LENGTH) {
        return { data: [], count: 0 };
      }

      const { data, count, error } = await supabase
        .from('teams_list')
        .select('*', { count: 'exact' })
        .ilike('team_name', `%${params.searchTerm.value}%`)
        .eq('schedule_year', 2025)
        .limit(5000)
        .order('team_name', { ascending: true });

      if (error) throw error;
      return { data, count };
    },
    enabled: computed(() => {
      return mode.value === 'teams' && params.searchTerm.value.length >= MIN_SEARCH_TERM_LENGTH;
    }),
  });

  return {
    data: computed(() => mode.value === 'players' ? playersQuery.data.value : teamsQuery.data.value),
    isLoading: computed(() => mode.value === 'players' ? playersQuery.isLoading.value : teamsQuery.isLoading.value),
  };
} 